import React, { useEffect, useRef, useState } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { RunnerAdd } from "../../services/runner.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Loader } from "@googlemaps/js-api-loader";

const AddRunner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Runner"), url: "/admin/temple-runner/list/1" },
    { title: t("Add"), url: "" },
  ];

  /* ================= VALIDATION ================= */
  const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

  const validationSchema = Yup.object().shape({
    runnerName: Yup.string()
      .required("Runner name is required")
      .test(
        "no-only-spaces",
        "Runner name cannot be empty",
        (value) => value && value.trim() !== ""
      ),
    runnerEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .test(
        "no-only-spaces",
        "Email cannot be empty",
        (value) => value && value.trim() !== ""
      ),
    runnerPhoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "no-only-spaces",
        "Phone number cannot be empty",
        (value) => value && value.trim() !== ""
      ),
    city: Yup.string()
      .required("City is required")
      .test(
        "no-only-spaces",
        "City cannot be empty",
        (value) => value && value.trim() !== ""
      ),
    state: Yup.string()
        .nullable(),
    country: Yup.string()
        .nullable(),
    runnerImage: Yup.mixed()
      .required("Runner image is required")
      .test("fileFormat", "Only JPG, JPEG, PNG files are allowed", (value) => {
        if (!value) return false;
        return SUPPORTED_FORMATS.includes(value.type);
      }),
  });

  const formik = useFormik({
    initialValues: {
      runnerName: "",
      runnerEmail: "",
      runnerPhoneNumber: "",
      runnerImage: "",
      city: "",
      state: null,     
      country: null,   
      latitude: "",
      longitude: "",
    },
    validationSchema,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      console.log(values);
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => formData.append(key, values[key]));

      RunnerAdd(formData)
        .then((response) => {
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            resetForm();
            navigate("/admin/temple-runner/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            setSubmitted(false);
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitted(false);
          Swal.fire({
            icon: "error",
            text:
              error?.response?.data?.message ||
              error?.message ||
              "Server Error",
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  /* ================= GOOGLE PLACES ================= */
  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
      version: "weekly",
      libraries: ["places"],
    });

    loader
      .importLibrary("places")
      .then(({ Autocomplete }) => {
        const autocomplete = new Autocomplete(inputRef.current, {
          fields: ["formatted_address", "geometry", "address_components"],
          types: ["geocode"],
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;

          const comps = place.address_components || [];
          const getComp = (type) =>
            comps.find((c) => c.types.includes(type))?.long_name || "";

          const city =
            getComp("locality") || getComp("administrative_area_level_2") ||  place.formatted_address;
          const state = getComp("administrative_area_level_1");
          const country = getComp("country");

          if (city) {
            formik.setFieldValue("city", city);
          }
          formik.setFieldValue("state", state);
          formik.setFieldValue("country", country);
          formik.setFieldValue("latitude", place.geometry.location.lat());
          formik.setFieldValue("longitude", place.geometry.location.lng());
        });
      })
      .catch((err) => console.error("Google Maps error:", err));
  }, []);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("Add Runner")}</h6>

                <div className="row row-sm">
                  {/* Runner Name */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      Runner Name <span className="requirestar"> *</span>
                    </label>
                    <input
                      name="runnerName"
                      className="form-control"
                      onChange={formik.handleChange}
                      value={formik.values.runnerName}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="runnerName" form={formik} />
                    </span>
                  </div>

                  {/* Email */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      Email <span className="requirestar"> *</span>
                    </label>
                    <input
                      name="runnerEmail"
                      className="form-control"
                      onChange={formik.handleChange}
                      value={formik.values.runnerEmail}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="runnerEmail" form={formik} />
                    </span>
                  </div>

                  {/* Phone */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      Phone <span className="requirestar"> *</span>
                    </label>
                    <input
                      name="runnerPhoneNumber"
                      className="form-control"
                      onChange={formik.handleChange}
                      value={formik.values.runnerPhoneNumber}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="runnerPhoneNumber" form={formik} />
                    </span>
                  </div>

                  {/* City Autocomplete */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      City <span className="requirestar"> *</span>
                    </label>
                    <input
                      name="city"
                      ref={inputRef}
                      className="form-control"
                      placeholder="Enter city"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="city" form={formik} />
                    </span>
                  </div>

                  {/* State */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      State <span className="requirestar"> *</span>
                    </label>
                    <input
                      className="form-control"
                      value={formik.values.state}
                      readOnly
                    />
                  </div>

                  {/* Country */}
                  <div className="col-md-6 form-group">
                    <label className="d-flex">
                      Country <span className="requirestar"> *</span>
                    </label>
                    <input
                      className="form-control"
                      value={formik.values.country}
                      readOnly
                    />
                  </div>

                  {/* Image */}
                  <div className="col-md-12 form-group">
                    <label className="d-flex">
                      Runner Image <span className="requirestar"> *</span>
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={(e) =>
                        formik.setFieldValue(
                          "runnerImage",
                          e.currentTarget.files[0],
                        )
                      }
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="runnerImage" form={formik} />
                    </span>
                  </div>

                  {/* Buttons */}
                  <div className="mt-3">
                    <button
                      className="btn btn-info me-2"
                      type="submit"
                      disabled={submitted}
                    >
                      Submit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddRunner;
